/**
 * CoachCard — learner dashboard widget that shows overall learning status
 * and one coached next-session recommendation.
 *
 * Structure:
 *   1. Snapshot card: "N subjects tracked" + journey bar + windowed stats
 *   2. Next-session card: the coach's highest-ranked suggestion
 *
 * Live data:
 *   - `useMyStats(windowDays)` — hero %, journey-bar distribution, windowed stats
 *   - `useMySuggestions(windowDays)` — suggestion list, already ordered by the
 *     coach service (see @formency/core coachService)
 *   - `useCreateReviseSession` — topic-click handler creates a node-scoped revise
 *
 * Click routing (per suggestion target type):
 *   - `lesson` + kind=course   → /courses/:courseId/:lessonId (learn session)
 *   - `lesson` + kind=personal → /lessons/unfiled/:lessonId   (personal session)
 *   - `topic`                  → createReviseSession({ nodeIds: [id] }) → /revision/session
 *
 * NOTE: The journey-bar distribution + cumulative mastery % come from a mock
 * for now — the API doesn't yet expose a per-level library distribution.
 * Tracking as a TODO; once the endpoint lands we'll replace the mock block.
 */
import { useMemo, useState } from 'react';
import { Button as AriaButton } from 'react-aria-components';
import { ArrowRight, Clock3, Play } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import type { LibraryDistribution, NudgeType, Suggestion } from '@formency/shared';
import {
  useCreateReviseSession,
  useDismissSuggestion,
  useMyLibraryDistribution,
  useMyStats,
  useMySuggestions,
  useRevisableContent,
} from '@/shared/api/hooks';
import type { StatsWindow } from '@/shared/api/hooks/useStats';
import { useSessionGuard } from '@/shared/hooks/useSessionGuard';
import { formatDurationMs } from '@/shared/utils/formatDuration';
import { Button, Tooltip, TooltipTrigger } from '@/interfaces/learner/ui';
import { eyebrow, meta, title as dsTitle } from '../styles';
import CreateRevisionAreaDialog from './CreateRevisionAreaDialog';

// ── Journey bar level config ─────────────────────────────────────────────────

interface LevelSlice {
  level: 'unstarted' | 'introduced' | 'practised' | 'confident' | 'mastered';
  label: string;
  count: number;
  color: string;
}

/**
 * Display metadata for each ladder level. `unstarted` shows as a leading
 * grey segment so the bar's total always equals subjects.length (matches
 * the headline "N subjects tracked"). Without it, subjects with no
 * responses simply disappeared from the bar — leaving e.g. 46 segments for
 * a 47-tracked headline, a small but real cognitive friction.
 *
 * Colour ladder: grey (untouched) → sky blue (exploring) → amber (warming
 * up) → teal (good) → deep green (best). Every coloured segment is a step
 * forward. The grey segment doesn't read as failure — it reads as
 * "available to start".
 */
const LEVEL_META: Array<Omit<LevelSlice, 'count'>> = [
  { level: 'unstarted', label: 'Not started', color: 'var(--color-surface-muted)' },
  { level: 'introduced', label: 'First look', color: '#8bb5d6' },
  { level: 'practised', label: 'Making progress', color: 'var(--color-warning)' },
  { level: 'confident', label: 'Confident', color: '#3fb590' },
  { level: 'mastered', label: 'Mastered', color: '#0e6d4a' },
];

/** Zero-filled distribution used while the API response is loading. */
const EMPTY_DISTRIBUTION: LibraryDistribution = {
  unstarted: 0,
  introduced: 0,
  practised: 0,
  confident: 0,
  mastered: 0,
  total: 0,
  started: 0,
  subjects: [],
  horizon: 0,
  stale: false,
  lastComputedAt: null,
};

function slicesFromDistribution(dist: LibraryDistribution): LevelSlice[] {
  return LEVEL_META.map((meta) => ({ ...meta, count: dist[meta.level] }));
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function nudgeMeta(n: NudgeType): { color: string; label: string } {
  switch (n) {
    case 'refresh':
      return { color: 'var(--color-info)', label: 'DUE' };
    case 'breakthrough':
      return { color: 'var(--color-error)', label: 'DIP' };
    case 'push':
      return { color: 'var(--color-warning)', label: 'PUSH' };
    case 'build':
      return { color: 'var(--color-accent)', label: 'FOCUS' };
    case 'start':
      return { color: 'var(--color-success)', label: 'NEW' };
  }
}

function windowLabel(v: StatsWindow): string {
  switch (v) {
    case 1:
      return 'Today';
    case 7:
      return 'This week';
    case 30:
      return 'Last 30 days';
    case 90:
      return 'Last 3 months';
    case 365:
      return 'Last year';
    case 'all':
      return 'All time';
  }
}

// ── Journey bar ──────────────────────────────────────────────────────────────

function JourneyBar({ slices }: { slices: LevelSlice[] }) {
  const total = slices.reduce((s, x) => s + x.count, 0) || 1;
  return (
    <div
      style={{
        display: 'flex',
        height: 16,
        borderRadius: 8,
        overflow: 'hidden',
        background: 'var(--color-surface-muted)',
        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.04)',
      }}
    >
      {slices.map((s, i) => {
        if (s.count === 0) return null;
        const pct = (s.count / total) * 100;
        const pctOfLibrary = Math.round((s.count / total) * 100);
        return (
          <TooltipTrigger key={s.level} delay={200} closeDelay={0}>
            <AriaButton
              aria-label={`${s.count} ${s.label} (${pctOfLibrary}%)`}
              style={{
                flex: `0 0 ${pct}%`,
                background: s.color,
                borderLeft: i > 0 ? '1px solid rgba(255,255,255,0.4)' : undefined,
                borderTop: 'none',
                borderRight: 'none',
                borderBottom: 'none',
                padding: 0,
                margin: 0,
                cursor: 'help',
                outline: 'none',
                transition: 'filter 0.15s',
                height: '100%',
              }}
              className="[&[data-hovered]]:brightness-110 [&[data-focus-visible]]:brightness-110"
            />
            <Tooltip>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 6,
                  fontFamily: 'var(--font-sans)',
                }}
              >
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    letterSpacing: 'var(--tracking-normal)',
                  }}
                >
                  {s.count}
                </span>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    opacity: 0.8,
                  }}
                >
                  {s.label}
                </span>
                <span style={{ fontSize: 11, fontWeight: 500, opacity: 0.6, marginLeft: 2 }}>
                  · {pctOfLibrary}%
                </span>
              </div>
            </Tooltip>
          </TooltipTrigger>
        );
      })}
    </div>
  );
}

// ── Windowed stats row ───────────────────────────────────────────────────────

function WindowedStatsRow({
  newLessons,
  accuracyPct,
  accuracyDeltaPct,
  timeLabel,
}: {
  newLessons: number;
  accuracyPct: number | null;
  accuracyDeltaPct: number | null;
  timeLabel: string;
}) {
  const accuracyUp = (accuracyDeltaPct ?? 0) >= 0;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 8,
        flexWrap: 'wrap',
        fontFamily: 'var(--font-sans)',
      }}
    >
      <Metric value={`${newLessons}`} label={newLessons === 1 ? 'new lesson' : 'new lessons'} />
      <Dot />
      <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4 }}>
        <span
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: 'var(--color-text)',
            letterSpacing: 'var(--tracking-normal)',
          }}
        >
          {accuracyPct != null ? `${accuracyPct}%` : '—'}
        </span>
        <span style={{ ...meta, fontSize: 12 }}>accuracy</span>
        {accuracyDeltaPct != null && accuracyDeltaPct !== 0 && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: accuracyUp ? 'var(--color-success)' : 'var(--color-error)',
            }}
          >
            {accuracyUp ? '↑' : '↓'}
            {Math.abs(accuracyDeltaPct)}
          </span>
        )}
      </span>
      <Dot />
      <Metric value={timeLabel} label="studied" />
    </div>
  );
}

function Metric({ value, label }: { value: string; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4 }}>
      <span
        style={{
          fontSize: 14,
          fontWeight: 700,
          color: 'var(--color-text)',
          letterSpacing: 'var(--tracking-normal)',
        }}
      >
        {value}
      </span>
      <span style={{ ...meta, fontSize: 12 }}>{label}</span>
    </span>
  );
}

function Dot() {
  return <span style={{ color: 'var(--color-text-muted)', fontSize: 11 }}>·</span>;
}

// ── Next session card ────────────────────────────────────────────────────────

function suggestionSessionLabel(s: Suggestion): string {
  if (s.target.type === 'lesson') return 'Learn session';
  return 'Revision session';
}

function suggestionCtaLabel(s: Suggestion): string {
  if (s.target.type === 'lesson') return 'Start lesson';
  return 'Start revision';
}

function NudgePill({ nudge }: { nudge: NudgeType }) {
  const n = nudgeMeta(nudge);
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: '0.08em',
        padding: '5px 8px',
        borderRadius: 7,
        background: n.color,
        color: 'var(--color-text-on-accent)',
        minWidth: 52,
        flexShrink: 0,
      }}
    >
      {n.label}
    </span>
  );
}

function NextSessionCard({
  suggestion,
  isLoading,
  isStarting,
  onStart,
  onCreateRevisionSession,
}: {
  suggestion: Suggestion | null;
  isLoading: boolean;
  isStarting: boolean;
  onStart: (s: Suggestion) => void;
  onCreateRevisionSession: () => void;
}) {
  return (
    <section className="coach-card coach-card--next" aria-labelledby="coach-next-session-heading">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <span id="coach-next-session-heading" style={{ ...eyebrow, fontSize: 12 }}>
          Next session
        </span>
        <span style={{ ...meta, fontSize: 12 }}>Recommended now</span>
      </div>

      {isLoading && !suggestion ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <span style={{ ...dsTitle, fontSize: 18 }}>Finding your best next move</span>
          <span style={{ ...meta, fontSize: 13 }}>
            Checking your recent lessons and revision history.
          </span>
        </div>
      ) : suggestion ? (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <NudgePill nudge={suggestion.nudge} />
              <span style={{ ...meta, fontSize: 12 }}>{suggestionSessionLabel(suggestion)}</span>
              <span
                style={{
                  ...meta,
                  fontSize: 12,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <Clock3 size={13} strokeWidth={2} />
                {suggestion.estMinutes} min
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
              <span
                style={{
                  ...dsTitle,
                  fontSize: 20,
                  lineHeight: 1.18,
                  overflowWrap: 'anywhere',
                }}
              >
                {suggestion.title}
              </span>
              <span
                style={{
                  ...meta,
                  fontSize: 13,
                  lineHeight: 1.4,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {suggestion.reason}
              </span>
              {suggestion.path && (
                <span
                  style={{
                    ...meta,
                    fontSize: 11,
                    lineHeight: 1.35,
                    color: 'var(--color-text-muted)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {suggestion.path}
                </span>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Button
              variant="primary"
              size="lg"
              icon={<Play size={16} strokeWidth={2.5} fill="currentColor" />}
              iconRight={<ArrowRight size={15} strokeWidth={2.2} />}
              onPress={() => onStart(suggestion)}
              loading={isStarting}
              className="w-full"
              style={{ width: '100%' }}
            >
              {suggestionCtaLabel(suggestion)}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onPress={onCreateRevisionSession}
              className="w-full"
              style={{ width: '100%' }}
            >
              Create revision session
            </Button>
          </div>
        </>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={{ ...dsTitle, fontSize: 18 }}>No session suggested yet</span>
            <span style={{ ...meta, fontSize: 13, lineHeight: 1.4 }}>
              Start a lesson or create a revision session and the coach will prioritize the next
              one.
            </span>
          </div>
          <Button
            variant="primary"
            size="lg"
            onPress={onCreateRevisionSession}
            className="w-full"
            style={{ width: '100%' }}
          >
            Create revision session
          </Button>
        </>
      )}
    </section>
  );
}

// ── Main card ────────────────────────────────────────────────────────────────

export default function CoachCard() {
  const navigate = useNavigate();
  const [windowValue, setWindowValue] = useState<StatsWindow>(7);

  // Live data ────────────────────────────────────────────────────────────────
  const { data: distribution } = useMyLibraryDistribution();
  const slices = useMemo(
    () => slicesFromDistribution(distribution ?? EMPTY_DISTRIBUTION),
    [distribution]
  );
  // Derived from the snapshot — preserves the old "% mastered" hero shape
  // without re-introducing the dropped masteredPct field. Denominator is
  // `started`, not `total`, so new enrolments don't drop the headline.
  const masteredCount = distribution?.mastered ?? 0;
  const startedCountForPct = distribution?.started ?? 0;
  const masteredPct =
    startedCountForPct > 0 ? Math.round((masteredCount / startedCountForPct) * 100) : 0;

  /**
   * Hero mode — pick between percentage display and absolute count. We switch
   * to count when the percentage would round too low to be meaningful (i.e.
   * the learner HAS mastered topics but the headline would read as "0%"
   * because the denominator is large). Shows "3 topics mastered" instead of
   * "0% mastered" — same information, much better emotional register.
   *
   * Rules:
   *   - mastered == 0  → "Working on N topics" (no percentage)
   *   - masteredPct rounds <5 but count > 0 → absolute count
   *   - otherwise → percentage
   */
  const heroMode: 'working' | 'count' | 'percent' =
    masteredCount === 0 ? 'working' : masteredPct < 5 ? 'count' : 'percent';
  // Headline count uses subjects.length (the "tracked" number) so the hero
  // matches the Subjects section subtitle ("N tracked · M more in library").
  // Previously used `started` (subjects with ≥1 response), which differs by
  // the number of zero-response subjects in the snapshot — typically domains
  // the user hasn't engaged with yet — and produced a "46 vs 47" mismatch
  // visible in the same card.
  const trackedCount = distribution?.subjects.length ?? 0;

  const { data: statsData } = useMyStats({ windowDays: windowValue });
  const accuracyPct = statsData?.performance?.accuracyPct ?? null;
  const accuracyDeltaPct = statsData?.performance?.accuracyDeltaPct ?? null;
  const lessonsThisWindow = statsData?.lessonsThisWeek ?? 0;
  const learningTimeMs = statsData?.learningTime?.totalMs ?? 0;
  const timeLabel = formatDurationMs(learningTimeMs);

  // Suggestions are NOT scoped by the window picker. "What should I revise
  // next?" is the coach's answer; it's a function of the learner's state, not
  // the UI time-period filter. The picker only affects the activity stats row
  // below (new lessons / accuracy / time studied). The server still applies
  // its own internal window for trend signals (default 7 days) — the right
  // answer is fixed per user state, not per UI picker.
  const { data: suggestionsData, isLoading: suggestionsLoading } = useMySuggestions();

  const suggestions = useMemo(
    () => suggestionsData?.suggestions ?? [],
    [suggestionsData?.suggestions]
  );
  const nextSuggestion = suggestions[0] ?? null;

  // Session creation for topic-target suggestions.
  const createReviseSession = useCreateReviseSession();
  const dismissSuggestion = useDismissSuggestion();
  const { guardSession } = useSessionGuard();

  // Revisable content for the "By content" tab in the create-revision-session dialog.
  const { data: revisableData } = useRevisableContent();
  const revisableCourses = useMemo(() => revisableData?.courses ?? [], [revisableData]);
  const revisablePersonalLessons = useMemo(
    () => revisableData?.personalLessons ?? [],
    [revisableData]
  );

  const [revisionAreaOpen, setRevisionAreaOpen] = useState(false);

  const handleSuggestionClick = (s: Suggestion) => {
    // Suppress this suggestion on the dashboard for 2 hours (server-side so
    // it carries across devices). Fire-and-forget — failure here shouldn't
    // block the click; worst case the suggestion re-appears on next refresh.
    dismissSuggestion.mutate({ suggestionId: s.id });

    if (s.target.type === 'lesson') {
      if (s.target.kind === 'personal') {
        navigate(`/lessons/unfiled/${s.target.lessonId}`);
      } else if (s.target.courseId) {
        navigate(`/courses/${s.target.courseId}/${s.target.lessonId}`);
      } else {
        toast.error('Could not open this lesson');
      }
      return;
    }
    // Topic target: resolve to step versions server-side and start revise.
    // Capture the nodeId locally so the closure below keeps the narrowed type.
    const nodeId = s.target.nodeId;
    guardSession({
      onProceed: () => {
        createReviseSession.mutate(
          { lessonIds: [], nodeIds: [nodeId] },
          {
            onSuccess: () => navigate('/revision/session', { state: { newSession: true } }),
            onError: (err) => toast.error(err.message),
          }
        );
      },
    });
  };

  // "Create revision session" dialog callbacks.
  const handleStartByLessons = (config: {
    lessonIds: string[];
    questionTypes: string[];
    maxQuestions: number;
  }) => {
    setRevisionAreaOpen(false);
    guardSession({
      onProceed: () => {
        createReviseSession.mutate(config, {
          onSuccess: () => navigate('/revision/session', { state: { newSession: true } }),
          onError: (err) => toast.error(err.message),
        });
      },
    });
  };

  const handleStartByTopic = (config: {
    nodeIds: string[];
    questionTypes: string[];
    maxQuestions: number;
  }) => {
    if (config.nodeIds.length === 0) return;
    setRevisionAreaOpen(false);
    guardSession({
      onProceed: () => {
        createReviseSession.mutate(
          {
            lessonIds: [],
            nodeIds: config.nodeIds,
            questionTypes: config.questionTypes,
            maxQuestions: config.maxQuestions,
          },
          {
            onSuccess: () => navigate('/revision/session', { state: { newSession: true } }),
            onError: (err) => toast.error(err.message),
          }
        );
      },
    });
  };

  return (
    <aside className="coach-stack">
      <section className="coach-card coach-card--snapshot" aria-labelledby="coach-snapshot-heading">
        {/* Header — eyebrow + window picker */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <span id="coach-snapshot-heading" style={{ ...eyebrow, fontSize: 12 }}>
            Your learning
          </span>
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              cursor: 'pointer',
              position: 'relative',
            }}
          >
            <select
              value={String(windowValue)}
              onChange={(e) => {
                const v = e.target.value;
                setWindowValue(v === 'all' ? 'all' : (Number(v) as StatsWindow));
              }}
              style={{
                position: 'absolute',
                inset: 0,
                opacity: 0,
                cursor: 'pointer',
                appearance: 'none',
                WebkitAppearance: 'none',
              }}
              aria-label="Time window"
            >
              {([1, 7, 30, 90, 365, 'all'] as const).map((v) => (
                <option key={String(v)} value={String(v)}>
                  {windowLabel(v)}
                </option>
              ))}
            </select>
            <span
              style={{
                ...meta,
                fontSize: 12,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              {windowLabel(windowValue)}
              <svg
                width="8"
                height="8"
                viewBox="0 0 8 8"
                aria-hidden="true"
                style={{ opacity: 0.6 }}
              >
                <path d="M1 2.5 L4 5.5 L7 2.5" stroke="currentColor" strokeWidth="1" fill="none" />
              </svg>
            </span>
          </label>
        </div>

        {/* Cumulative state — adaptive hero, see `heroMode` above. */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
            {heroMode === 'percent' && (
              <>
                <span
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: 38,
                    fontWeight: 800,
                    letterSpacing: 'var(--tracking-normal)',
                    lineHeight: 1,
                    color: 'var(--color-text)',
                  }}
                >
                  {masteredPct}%
                </span>
                <span
                  style={{
                    fontSize: 14,
                    color: 'var(--color-text-secondary)',
                    fontWeight: 500,
                    letterSpacing: 'var(--tracking-normal)',
                  }}
                >
                  mastered
                </span>
              </>
            )}

            {heroMode === 'count' && (
              <>
                <span
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: 38,
                    fontWeight: 800,
                    letterSpacing: 'var(--tracking-normal)',
                    lineHeight: 1,
                    color: 'var(--color-text)',
                  }}
                >
                  {masteredCount}
                </span>
                <span
                  style={{
                    fontSize: 14,
                    color: 'var(--color-text-secondary)',
                    fontWeight: 500,
                    letterSpacing: 'var(--tracking-normal)',
                  }}
                >
                  {masteredCount === 1 ? 'topic mastered' : 'topics mastered'}
                </span>
              </>
            )}

            {heroMode === 'working' && (
              <>
                <span
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: 38,
                    fontWeight: 800,
                    letterSpacing: 'var(--tracking-normal)',
                    lineHeight: 1,
                    color: 'var(--color-text)',
                  }}
                >
                  {trackedCount}
                </span>
                <span
                  style={{
                    fontSize: 14,
                    color: 'var(--color-text-secondary)',
                    fontWeight: 500,
                    letterSpacing: 'var(--tracking-normal)',
                  }}
                >
                  {trackedCount === 1 ? 'subject tracked' : 'subjects tracked'}
                </span>
              </>
            )}

            {/* Historical-delta chip removed alongside masteredDelta in the
              snapshot reshape. The "this week" stats below now carry the
              previous-period comparisons (Retention · New lessons · Time
              studied) instead. */}
            {distribution?.horizon != null && distribution.horizon > 0 && (
              <span style={{ ...meta, fontSize: 13 }}>
                · {distribution.horizon} more in your library
              </span>
            )}
          </div>
          {/* Scope note. Only shown in percent mode — the scope would be obvious
            ("working on N topics" IS the scope) otherwise. */}
          {heroMode === 'percent' && (
            <span style={{ ...meta, fontSize: 12 }}>of topics you&apos;ve studied</span>
          )}
          <JourneyBar slices={slices} />
        </div>

        {/* Subjects list removed — the headline + journey bar already convey
          the cumulative state, and the revision-session dialog gives a
          richer, browsable view when the learner wants one. */}

        {/* Windowed activity */}
        <WindowedStatsRow
          newLessons={lessonsThisWindow}
          accuracyPct={accuracyPct}
          accuracyDeltaPct={accuracyDeltaPct}
          timeLabel={timeLabel}
        />
      </section>

      <NextSessionCard
        suggestion={nextSuggestion}
        isLoading={suggestionsLoading}
        isStarting={createReviseSession.isPending}
        onStart={handleSuggestionClick}
        onCreateRevisionSession={() => setRevisionAreaOpen(true)}
      />

      <CreateRevisionAreaDialog
        isOpen={revisionAreaOpen}
        onClose={() => setRevisionAreaOpen(false)}
        courses={revisableCourses}
        personalLessons={revisablePersonalLessons}
        onStartByLessons={handleStartByLessons}
        onStartByTopic={handleStartByTopic}
        isCreating={createReviseSession.isPending}
        error={createReviseSession.error?.message}
      />
      <style>{`
        .coach-stack {
          display: flex;
          flex-direction: column;
          gap: 16px;
          width: 100%;
          min-height: 0;
          box-sizing: border-box;
        }

        .coach-card {
          display: flex;
          flex-direction: column;
          width: 100%;
          padding: var(--dashboard-rail-padding);
          border: 1px solid var(--dashboard-rail-border);
          border-radius: var(--dashboard-panel-radius);
          background: var(--dashboard-rail-bg);
          box-shadow: var(--dashboard-panel-shadow);
          box-sizing: border-box;
        }

        .coach-card--snapshot {
          gap: 16px;
        }

        .coach-card--next {
          gap: 18px;
        }
      `}</style>
    </aside>
  );
}
